import React, { useState } from 'react';
import { 
  XMarkIcon,
  SparklesIcon,
  DocumentIcon,
  ChevronLeftIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import { MenuItem as AgentMenuItem } from './agents/AgentNode';
import { MenuItem as TriggerMenuItem } from './TriggerNode';
import { MenuItem as GetDataMenuItem } from './documents/data/GetDataNode';
import { MenuItem as UpdateDataMenuItem } from './documents/data/UpdateDataNode';
import { MenuItem as CreateDataMenuItem } from './documents/data/CreateDataNode';
import { MenuItem as CreateDocumentMenuItem } from './documents/docs/CreateDocumentNode';
import { MenuItem as UpdateDocumentMenuItem } from './documents/docs/UpdateDocumentNode';
import { MenuItem as GetDocumentMenuItem } from './documents/docs/GetDocumentNode';
import { MenuItem as SocialMenuItem } from './social/SocialNode';

export default function AddItemDrawer({ isOpen, onClose, onAdd, existingNodes }) {
  const [showDocumentNodes, setShowDocumentNodes] = useState(false);

  // Define all available nodes
  const allNodes = {
    trigger: {
      component: TriggerMenuItem,
      type: 'trigger',
      name: 'Trigger',
      category: 'trigger'
    },
    agent: {
      component: AgentMenuItem,
      type: 'agent',
      name: 'AI Agent',
      category: 'agent'
    },
    documents: {
      type: 'menu',
      name: 'Documents',
      category: 'documents',
      items: {
        createData: {
          component: CreateDataMenuItem,
          type: 'createdata',
          name: 'Create Data Table',
          category: 'document'
        },
        updateData: {
          component: UpdateDataMenuItem,
          type: 'updatedata',
          name: 'Update Data Table',
          category: 'document'
        },
        getData: {
          component: GetDataMenuItem,
          type: 'getdata',
          name: 'Get Data Table',
          category: 'document'
        },
        createDocument: {
          component: CreateDocumentMenuItem,
          type: 'createdocument',
          name: 'Create Document',
          category: 'document'
        },
        updateDocument: {
          component: UpdateDocumentMenuItem,
          type: 'updatedocument',
          name: 'Update Document',
          category: 'document'
        },
        getDocument: {
          component: GetDocumentMenuItem,
          type: 'getdocument',
          name: 'Get Document',
          category: 'document'
        }
      }
    },
    social: {
      component: SocialMenuItem,
      type: 'social',
      name: 'Social Media',
      category: 'social'
    }
  };

  if (!isOpen) return null;

  // Check if trigger node already exists
  const hasTrigger = existingNodes.some(node => node.type === 'trigger');

  const handleNodeClick = (node) => {
    if (node.type === 'menu') {
      setShowDocumentNodes(true);
    } else {
      onAdd({
        ...node,
        label: node.name
      });
    }
  };

  const renderMenuItem = (node) => {
    if (node.component) {
      const Component = node.component;
      return (
        <Component
          onSelect={() => handleNodeClick(node)}
        />
      );
    }

    const getIcon = () => {
      switch (node.type) {
        case 'menu':
          return <DocumentIcon className="w-5 h-5 text-gray-400" />;
        case 'social':
          return <ShareIcon className="w-5 h-5 text-gray-400" />;
        default:
          return <SparklesIcon className="w-5 h-5 text-gray-400" />;
      }
    };

    return (
      <div className="group">
        <button
          onClick={() => handleNodeClick(node)}
          className="w-full p-3 flex items-center gap-3 rounded-lg hover:bg-dark-300"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-dark-300 group-hover:bg-dark-400 rounded-lg flex items-center justify-center">
            {getIcon()}
          </div>
          <div className="flex-1 text-left">
            <div className="font-medium text-sm">{node.name}</div>
            <div className="text-sm text-gray-400">
              {node.type === 'menu' 
                ? 'Create, update and load Text Documents & Data Table Operations' 
                : node.description || 'AI Processing Node'}
            </div>
          </div>
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-dark-200 border-l border-divider shadow-xl z-50">
      <div className="flex items-center justify-between p-4 border-b border-divider">
        <div className="flex items-center gap-2">
          {showDocumentNodes && (
            <button
              onClick={() => setShowDocumentNodes(false)}
              className="p-1 rounded-lg hover:bg-dark-300 text-gray-400 hover:text-white"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          )}
          <h2 className="text-lg font-medium">
            {showDocumentNodes ? 'Document Nodes' : 'Add Item'}
          </h2>
        </div>
        <button 
          onClick={onClose} 
          className="text-gray-400 hover:text-gray-300"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      <div className="p-4">
        <div className="space-y-2">
          {showDocumentNodes ? (
            // Show document and data table nodes
            Object.values(allNodes.documents.items).map((node) => (
              <div key={node.type}>
                {renderMenuItem(node)}
              </div>
            ))
          ) : (
            // Show main menu
            <>
              {!hasTrigger && renderMenuItem(allNodes.trigger)}
              {renderMenuItem(allNodes.agent)}
              {renderMenuItem(allNodes.documents)}
              {renderMenuItem(allNodes.social)}
            </>
          )}
        </div>
      </div>
    </div>
  );
} 